import { Component, Input, Output, OnInit,EventEmitter, OnDestroy } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';

import { ProjectsEditorService, ProjectsService } from '@app/services/app/projects.service';
import { AppService } from '@app/services/app/app.service';
import { AppRouterService } from '@app/services/app/router.service';
import { AccessProject, AccessProjectKeys } from '@app/models/access';


import * as Objects from '@core/objects';
import { AccountSession } from '@core/objects/account-session';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';

import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';


@Component({
    selector: 'project-details-info-attachments-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss']
})
export class ProjectDetailsInfoAttachmentsListComponent implements OnInit, OnDestroy {

    protected readonly PLUGIN_SEND_FILES = 'share_transfer';
    protected readonly APP_FILECOMPARISON = 'filecomparison';
    protected readonly ACCESS_GLOBAL_XRAY = 'access_global_xray';
    protected readonly PLUGIN_APOLLO = 'apollo'; // transitional: also gate "Compare" on this until apollo is fully retired

    @Input() project: Objects.Request = null;
    @Input() files: Array<Objects.AmsFile> = [];
    @Input() notes: Array<Objects.AnnotationsCounter> = [];
    @Output() actionClick = new EventEmitter<Objects.AmsFile>();
    // TODO maybe import project to set url for notes...
    //      or set output notes btn click with file to redirect in father component


    public pluginsAccess: Array<string> = [];
    public appsAccess: Array<string> = [];
    public accessGlobalXray: boolean = true;
    public accessSend: boolean = false;
    // we need order here for multiple items on menu
    public menu: Array<ThreeDotsMenuItem> = [
        {
            title: "Download",
            key: "download",
            icon: "download",
            order: 1
        },
        {
            title: "See Notes",
            key: "notes",
            icon: "notes",
            order: 5
        }
    ];
    protected menuSendFiles: ThreeDotsMenuItem = {
        title: "Send",
        key: "send-files",
        icon: "send-files",
        order: 2
    };
    protected menuCompare: ThreeDotsMenuItem = {
        title: "Compare",
        key: "compare",
        icon: "compare",
        order: 3
    };
    protected menuComparison: ThreeDotsMenuItem = {
        title: "NEW Filecomparison",
        htmltitle: "<strong>NEW</strong> Filecomparison",
        key: "comparison",
        icon: "compare",
        order: 4
    };
    protected menuDeactivateFile: ThreeDotsMenuItem = {
        title: "Remove",
        key: "remove-files",
        icon: "remove-files",
        order: 6
    };

    public showInactives: boolean = false;
    public accessActivation: boolean = false;

    protected accessSubscriptor: Subscription;
    protected appSubscriptor: Subscription;

	constructor( public app: AppService,
        protected projectsSrv: ProjectsService,
        private editorSrv: ProjectsEditorService,
        private urlSrv: UrlService,
        private fileDownloadSrv: FileDownloadService,
        private notifSrv: NotificationsService,
        private appRouter: AppRouterService ){}



	ngOnInit(){
        // control access to activate/deactivat files in components
        this.accessSubscriptor = this.editorSrv.selectAccess(AccessProjectKeys.SET_ACTIVE_FILES)
            .subscribe((access: boolean) => {
                this.accessActivation = access;
                this.updateMenu(this.menuDeactivateFile,this.accessActivation)
            });

        this.accessSubscriptor = this.editorSrv.selectAccess(AccessProjectKeys.SEND_FILE)
            .subscribe((access: boolean) => {
                this.accessSend = access;
                this.setMenu();
            });

        this.appSubscriptor = combineLatest([this.app.getSession(), this.app.getAccountSession()]).subscribe( ([session, accountSession]: [any, AccountSession]) => {
            this.pluginsAccess = session.options.plugins;
            // apps list now comes from the accounts-api session; fall back to the legacy session field
            // in case the (separate, parallel) accounts-api call hasn't resolved yet
            this.appsAccess = accountSession?.current?.apps ?? session.options.organization.apps;
            this.accessGlobalXray = session.options.access?.hasOwnProperty(this.ACCESS_GLOBAL_XRAY) ? session.options.access[this.ACCESS_GLOBAL_XRAY] : true;
            this.updateMenu(this.menuCompare,this.checkCompareAccess());
            this.updateMenu(this.menuComparison,this.checkFilecomparisonAccess());
            this.setMenu();
        });
	}


    onClickDeactivate(file: Objects.AmsFile){
        console.debug("click deactivate",file,this.accessActivation);
        if(!this.accessActivation)
            return;
        this.projectsSrv.setActiveFile(file, false);
    }

    onMenuClick(menu: AmsFilesListItemMenuClick) {
        switch(menu.key) {
            case "download-comments": 
                // start notification to download the pdf with the annotations in it. We need the url for the prefix of the download and the file.
                this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILE_ANNOTATIONS_START, {id: this.project.id, details: { url: "/requests/"+this.project.id, amsfile: menu.file}});
                break;
            case "remove-files":
                this.onClickDeactivate(menu.file);
                break;
            case "notes":
                console.debug("emit action click for notes", menu.file);
                this.actionClick.emit(menu.file);
                break;
            case "download":
                this.fileDownloadSrv.downloadFile(menu.file);
                break;
            case "send-files":
                if (this.checkPluginAccess(this.PLUGIN_SEND_FILES))
                    this.appRouter.navigate('/p/transfer/'+this.project.id, { queryParams: { file: +menu.file.id} });
                break;
            case "compare":
                this.appRouter.navigate('/p/apollo/request/'+this.project.id);
                break;
            case "comparison":
                this.appRouter.navigate('/filecomparison/project/'+this.project.id+'/file/'+menu.file.uuid);
                break;
        }
    }

    onActionDownloadClick(file: Objects.AmsFile){
        this.fileDownloadSrv.downloadFile(file);
    }

    ngOnDestroy() {
        this.accessSubscriptor.unsubscribe();
        this.appSubscriptor.unsubscribe();
    }

    protected checkPluginAccess(plugin: string){
        return this.pluginsAccess.some((pa) => pa == plugin);
    }

    protected checkAppAccess(app: string){
        return this.appsAccess.some((a) => a == app);
    }

    protected checkCompareAccess(): boolean {
        return this.accessGlobalXray && this.checkPluginAccess(this.PLUGIN_APOLLO);
    }

    protected checkFilecomparisonAccess(): boolean {
        return this.accessGlobalXray && this.checkAppAccess(this.APP_FILECOMPARISON);
    }

    // this function check either remove or add item. based on subscribed events
    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }


    protected setMenu() : void {
        if(this.pluginsAccess == null)
            return
        
        this.updateMenu(this.menuSendFiles, this.checkPluginAccess(this.PLUGIN_SEND_FILES) && this.accessSend);

    }


    onRefreshSignaturesNeeded() {
        // we need to refresh the file list to update the signatures and their status, so we emit an event to father component to do it.
        this.projectsSrv.loadAttachments();
    }




}
